/**
 * Página de cambio de estado de albaranes/facturas
 * Permite asignar preparador y cambiar estado de pedidos
 */
import React, { useState, useContext, useEffect, useMemo } from "react";
import {
    Box,
    Card,
    TextField,
    Select,
    MenuItem,
    Button,
    Typography,
    Divider,
    CircularProgress,
    useTheme,
    useMediaQuery
} from "@mui/material";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import AssignmentIcon from "@mui/icons-material/Assignment";
import CategoryIcon from "@mui/icons-material/Category";
import PersonIcon from "@mui/icons-material/Person";
import TagIcon from "@mui/icons-material/Tag";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { StateContext } from "../../../components/context/StateProvider";
import { FieldLabel, ModalConfirmacion } from "../components";
import Utils from "../../../utils/Utils";
import Toasts from "../../../components/notificacion/Toasts";
import successSound from "../../../assets/audio/success.mp3";
import warningSound from "../../../assets/audio/warning.mp3";
import errorSound from "../../../assets/audio/error.mp3";
import OrderService from "../../../services/order.service";
import OrderStates from "../../../utils/OrderStates";
import AuthService from "../../../services/auth.service";
import Roles from "../../../config/roles";
import { OrderType, orderTypesWithLabelsSpecial } from "../../../models/Order";
import UsuariosService from "../../../services/usuarios.service";
import { ALTURA_BOTON, BOTON_PRIMARIO_SX } from "../../../components/ui/buttonStyles";

const Orders = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"), { noSsr: true });
    // getCurrentUser() devuelve un objeto nuevo cada vez: sin useMemo, el
    // efecto que lo usa como dependencia entra en bucle.
    const user = useMemo(() => AuthService.getCurrentUser(), []);
    const { state, updateState } = useContext(StateContext);

    const esAdmin = user.roles.includes(Roles.ADMIN.role);

    const [showConfirmation, setShowConfirmation] = useState(false);
    const [pedidoInfo, setPedidoInfo] = useState({ title: "", text: "" });
    const [order, setOrder] = useState("");
    const [orderType, setOrderType] = useState(OrderType.Albaran.valueOf());
    const [preparers, setPreparers] = useState([]);
    // DECISION: el rol decide el valor inicial, no si se llama al hook. Antes el useState
    // vivía dentro de un ternario (error rules-of-hooks) y getStatesJson() se recalculaba
    // en cada render; el inicializador perezoso solo lo evalúa al montar.
    const [orderState, setOrderStateType] = useState(() =>
        esAdmin ? OrderStates.getStatesJson()[0].value : OrderStates.getStatesJson()[1].value
    );
    // Admin elige cualquier estado (incluido FINALIZADO); almacén y reponedor, los únicos otros
    // roles de la ruta, solo En preparación y Preparado
    const [orderStateOptions] = useState(() =>
        esAdmin ? OrderStates.getStatesJson() : OrderStates.getStatesJson().slice(1, 3)
    );
    const [prepare, setPrepare] = useState("");
    // ADMIN siempre muestra select, NO ADMIN oculto por defecto (se muestra solo si no está en la lista)
    const [mostrarPreparador, setMostrarPreparador] = useState(esAdmin);

    useEffect(() => {
        const fetchPreparers = async () => {
            const preparersData = await initSelects("Elige preparador");
            setPreparers(preparersData);

            const esAdmin = user.roles.includes(Roles.ADMIN.role);
            const usuarioLogueado = user?.username?.toLowerCase() || "";
            const preparadorEncontrado = preparersData.find((p) => p.value === usuarioLogueado);

            if (esAdmin) {
                // ADMIN: siempre mostrar select para poder asignar a cualquiera
                setPrepare(preparadorEncontrado ? preparadorEncontrado.value : preparersData[0].value);
                setMostrarPreparador(true);
            } else if (preparadorEncontrado) {
                // Usuario NO admin encontrado: asignar y NO mostrar select
                setPrepare(preparadorEncontrado.value);
                setMostrarPreparador(false);
            } else {
                // Usuario NO admin NO encontrado: mostrar select para elegir
                setPrepare(preparersData[0].value);
                setMostrarPreparador(true);
            }
        };
        fetchPreparers();
    }, [user]);

    const handleInputFocus = () => {
        setOrder("");
        setPedidoInfo({ title: "", text: "" });
    };

    const initSelects = async (texto) => {
        let employees = await UsuariosService.listarUsuariosMap();

        let employeesMap = employees.map((employee) => {
            return { label: employee.nombre, value: employee.nombre.toLowerCase() };
        });

        employeesMap.unshift({ label: texto, value: "0" });
        return employeesMap;
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        let toasts;
        if (order !== "" && Utils.checkOrder(order) && prepare !== "") {
            updateState({ loading: true });
            try {
                let orderAssign = await OrderService.assignToOrder(
                    order,
                    null,
                    prepare,
                    "ventas",
                    orderState,
                    null,
                    undefined,
                    state.year,
                    null,
                    orderType,
                    null,
                    null
                );
                toasts = Toasts.generateToast(
                    `${order} ha sido cambiado al estado: <strong>${OrderStates.getState(orderState).toLowerCase()}</strong>`,
                    "success"
                );
                if (orderAssign.length > 0 && orderAssign[0].numero_pedido && orderAssign[0].localizacion) {
                    setPedidoInfo({
                        title: "Pedido preparado",
                        text: `El albaran <strong>${order}</strong> tiene el pedido <strong>${orderAssign[0].numero_pedido}</strong> preparado en la localizacion <strong>${orderAssign[0].localizacion}</strong>`
                    });
                    setShowConfirmation(true);
                    Utils.playAudio(warningSound);
                } else {
                    Utils.playAudio(successSound);
                }
            } catch (error) {
                toasts = Toasts.generateToast(error.response.data.message, "error");
                Utils.playAudio(errorSound);
            }
            updateState({ loading: false });
            setOrder("");
        } else if ((order !== "" && !Utils.checkOrder(order)) || order === "") {
            toasts = Toasts.generateToast("Formato de albarán inválido: SERIE-ALBARAN", "error");
            Utils.playAudio(errorSound);
        } else if (prepare === "") {
            toasts = Toasts.generateToast("No se ha añadido el preparador", "error");
            Utils.playAudio(errorSound);
        }

        updateState({ toasts: [...state.toasts, toasts] });
    };

    return (
        <Box id="sales" sx={{ width: "100%" }}>
            <Card sx={{ boxShadow: "0 2px 8px rgba(0,0,0,0.1)", borderRadius: 2 }}>
                <ModalConfirmacion
                    show={showConfirmation}
                    title={pedidoInfo.title}
                    text={pedidoInfo.text}
                    onHide={() => setShowConfirmation(false)}
                />

                {/* La página es solo un formulario, así que no hay sección de contenido
                    aparte: header y formulario comparten el mismo Box con p: 3 */}
                <Box sx={{ p: 3 }}>
                    <Typography
                        variant={isMobile ? "h6" : "h5"}
                        sx={{
                            fontWeight: 700,
                            color: "#212529",
                            mb: 0.5,
                            display: "flex",
                            alignItems: "center",
                            gap: 1
                        }}
                    >
                        <AssignmentTurnedInIcon sx={{ fontSize: isMobile ? 24 : 28 }} />
                        Pasar a preparación
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#6c757d" }}>
                        Asigna preparador y cambia el estado de un albarán o pedido
                    </Typography>

                    <Divider sx={{ my: 2 }} />

                    <Box
                        component="form"
                        onSubmit={handleSubmit}
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: { xs: 2, sm: 2.5 }
                        }}
                    >
                        {/* Tipo de orden */}
                        <Box>
                            <FieldLabel icon={CategoryIcon}>Tipo</FieldLabel>
                            <Select
                                id="list-order-type"
                                name="list-order-type"
                                value={orderType}
                                onChange={(e) => setOrderType(e.target.value)}
                                fullWidth
                                sx={{
                                    backgroundColor: "#fff",
                                    "& .MuiSelect-select": {
                                        py: { xs: 1.5, sm: 1.75 }
                                    }
                                }}
                            >
                                {orderTypesWithLabelsSpecial.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </Box>

                        {/* Estado */}
                        <Box>
                            <FieldLabel icon={AssignmentIcon}>Estado</FieldLabel>
                            <Select
                                id="order-state"
                                name="order-state"
                                value={orderState}
                                onChange={(e) => setOrderStateType(e.target.value)}
                                fullWidth
                                sx={{
                                    backgroundColor: "#fff",
                                    "& .MuiSelect-select": {
                                        py: { xs: 1.5, sm: 1.75 }
                                    }
                                }}
                            >
                                {orderStateOptions.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </Box>

                        {/* Preparador */}
                        {mostrarPreparador && (
                            <Box>
                                <FieldLabel icon={PersonIcon}>Preparador</FieldLabel>
                                <Select
                                    id="preparer"
                                    name="preparer"
                                    value={prepare}
                                    onChange={(e) => setPrepare(e.target.value)}
                                    fullWidth
                                    sx={{
                                        backgroundColor: "#fff",
                                        "& .MuiSelect-select": {
                                            py: { xs: 1.5, sm: 1.75 }
                                        }
                                    }}
                                >
                                    {preparers.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </Box>
                        )}

                        {/* Número de albarán/factura */}
                        <Box>
                            <FieldLabel icon={TagIcon}>Nº</FieldLabel>
                            <TextField
                                type="text"
                                id="order"
                                name="order"
                                value={order}
                                onFocus={handleInputFocus}
                                onChange={(e) => setOrder(e.target.value)}
                                fullWidth
                                placeholder="Ej: 00-12345"
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        backgroundColor: "#fff"
                                    },
                                    "& .MuiInputBase-input": {
                                        py: { xs: 1.5, sm: 1.75 }
                                    }
                                }}
                            />
                        </Box>

                        {/* Botón de envío */}
                        <Button
                            type="submit"
                            variant="contained"
                            fullWidth
                            disabled={state.loading}
                            startIcon={
                                state.loading ? (
                                    <CircularProgress size={20} sx={{ color: "#fff" }} />
                                ) : (
                                    <CheckCircleIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
                                )
                            }
                            sx={{ ...BOTON_PRIMARIO_SX, height: ALTURA_BOTON.FILTRO, mt: { xs: 1, sm: 0.5 } }}
                        >
                            {state.loading ? "Procesando..." : "Cambiar estado"}
                        </Button>
                    </Box>
                </Box>
            </Card>
        </Box>
    );
};

export default Orders;
